import { useEffect, useId, useState } from "react";
import { isLifePlace, lifeShopPath, organizeLifePlaces, type LifePlace, type LifeSection } from "../../models/life";
import { lifeSections as fallbackLifeSections } from "../../models/site";
import { ExternalLink } from "./ExternalLink";
import { PageIntro } from "./PageIntro";

type DisplayLink = {
  id?: string;
  name: string;
  href: string;
  note?: string;
};

type PlaceItemProps = {
  link: DisplayLink;
  expanded: boolean;
  onToggle: () => void;
};

function PlaceItem({ link, expanded, onToggle }: PlaceItemProps) {
  const noteId = useId();
  const shopHref = link.id ? lifeShopPath(link.id) : null;

  if (link.href === "#") return <span className="place-item-static">{link.name}</span>;

  return (
    <div className={`place-item${link.note ? " has-note" : ""}${expanded ? " note-open" : ""}`}>
      {shopHref ? (
        <a href={shopHref} className="place-name">{link.name}</a>
      ) : (
        <ExternalLink href={link.href} className="place-name">{link.name}</ExternalLink>
      )}
      {link.note && (
        <>
          <button
            type="button"
            className="place-note-trigger"
            aria-expanded={expanded}
            aria-controls={noteId}
            aria-label={`${link.name}的小星筆記`}
            onClick={onToggle}
          >
            <span aria-hidden="true">✦</span>
            <span className="place-note-label">小星筆記</span>
          </button>
          <div className="place-note-popover" id={noteId} role="note">
            <span>小星筆記</span>
            <p>{link.note}</p>
          </div>
        </>
      )}
    </div>
  );
}

export function LifePage({ initialPlaces = [] }: { initialPlaces?: LifePlace[] }) {
  const initialSections = organizeLifePlaces(initialPlaces);
  const [sections, setSections] = useState<LifeSection[] | undefined>(
    initialSections.length > 0 ? initialSections : undefined,
  );
  const [expandedNoteKey, setExpandedNoteKey] = useState<string | null>(null);

  useEffect(() => {
    if (initialPlaces.length > 0) return;
    const controller = new AbortController();
    void fetch("/api/life-in-dulan", { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Life content HTTP ${response.status}`);
        return response.json() as Promise<{ places?: unknown[] }>;
      })
      .then((payload) => {
        const places = payload.places?.filter(isLifePlace) ?? [];
        const organized = organizeLifePlaces(places);
        if (organized.length > 0) setSections(organized);
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          console.warn("Using built-in Life in Dulan content", error);
        }
      });
    return () => controller.abort();
  }, [initialPlaces.length]);

  useEffect(() => {
    if (expandedNoteKey === null) return;

    const closeNoteOutsideCard = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest(".place-item.note-open")) return;
      setExpandedNoteKey(null);
    };

    document.addEventListener("pointerdown", closeNoteOutsideCard);
    return () => document.removeEventListener("pointerdown", closeNoteOutsideCard);
  }, [expandedNoteKey]);

  const displayedSections = sections ?? fallbackLifeSections;

  return (
    <div className="standard-page life-page">
      <PageIntro eyebrow="OUR NEIGHBORHOOD" title="都蘭生活" en="LIFE IN DULAN">
        <p>
          從早晨的飯糰、午後咖啡，到海邊散步與夜裡觀星，慢慢感受都蘭的節奏。
        </p>
        <p className="life-note-hint">
          帶 ✦ 的店家有小星筆記；桌機滑過店名，手機點按即可查看。<br/>
          ❋ 營業時間請確認各店家最新官方公告 <br/>
          ❋ Please check each store’s official info for hours.
        </p>
      </PageIntro>
      {displayedSections.map((section, sectionIndex) => (
        <section className="life-section" key={section.en}>
          <div className="life-title">
            <span>0{sectionIndex + 1}</span>
            <h2>
              {section.title}
              <small>{section.en}</small>
            </h2>
          </div>
          <div className="life-groups">
            {section.groups.map((group, groupIndex) => (
              <article key={group.title}>
                <div className="life-group-heading">
                  <h3>{group.title}</h3>
                  {group.intro && <p>{group.intro}</p>}
                </div>
                <div className="place-links">
                  {group.links.map((link, linkIndex) => {
                    const displayLink = link as DisplayLink;
                    const noteKey = `${sectionIndex}:${groupIndex}:${displayLink.id ?? linkIndex}`;
                    return (
                      <PlaceItem
                        key={displayLink.id ?? displayLink.name}
                        link={displayLink}
                        expanded={expandedNoteKey === noteKey}
                        onToggle={() => {
                          setExpandedNoteKey((current) => current === noteKey ? null : noteKey);
                        }}
                      />
                    );
                  })}
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
